import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, Circle, AlertTriangle, XCircle, ArrowLeft, Radio } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useJobStatus } from '@/hooks/useJob';
import type { JobStatus } from '@/types';
import { cn } from '@/lib/utils';

const STATUS_STAGES: { key: JobStatus; label: string }[] = [
  { key: 'resolving_source', label: 'Resolving source' },
  { key: 'extracting_metadata', label: 'Extracting metadata' },
  { key: 'finding_transcript', label: 'Looking for transcript / captions' },
  { key: 'analyzing_segments', label: 'Analyzing segments' },
  { key: 'synthesizing', label: 'Synthesizing episode triage' },
  { key: 'fact_checking', label: 'Fact-checking claims (2-pass verification)' },
  { key: 'complete', label: 'Complete' },
];

function getStageIndex(status: JobStatus): number {
  const idx = STATUS_STAGES.findIndex((s) => s.key === status);
  return idx === -1 ? -1 : idx;
}

function StageIcon({ state }: { state: 'done' | 'active' | 'pending' }) {
  if (state === 'done') {
    return <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />;
  }
  if (state === 'active') {
    return <Loader2 className="h-5 w-5 text-primary shrink-0 animate-spin" />;
  }
  return <Circle className="h-5 w-5 text-muted-foreground/40 shrink-0" />;
}

export function StatusPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { job, loading, error } = useJobStatus(jobId);

  // Auto-navigate to results when complete
  useEffect(() => {
    if (job?.status === 'complete') {
      const timer = setTimeout(() => {
        navigate(`/jobs/${jobId}/results`, { replace: true });
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [job?.status, jobId, navigate]);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center gap-4">
              <XCircle className="h-12 w-12 text-destructive" />
              <div>
                <h2 className="text-lg font-semibold">Failed to load job</h2>
                <p className="text-sm text-muted-foreground mt-1">{error || 'Job not found.'}</p>
              </div>
              <Button asChild variant="outline">
                <Link to="/">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Home
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentStageIdx = getStageIndex(job.status);
  const isFailed = job.status === 'failed';

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center p-4">
      <div className="w-full max-w-xl">
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2">
              {isFailed ? (
                <XCircle className="h-10 w-10 text-destructive" />
              ) : job.status === 'complete' ? (
                <CheckCircle2 className="h-10 w-10 text-primary" />
              ) : (
                <Radio className="h-10 w-10 text-primary animate-pulse" />
              )}
            </div>
            <CardTitle>
              {isFailed ? 'Analysis Failed' : job.status === 'complete' ? 'Analysis Complete' : 'Analyzing Episode'}
            </CardTitle>
            {job.metadata?.episodeTitle && (
              <p className="text-sm text-muted-foreground mt-1 truncate">{job.metadata.episodeTitle}</p>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Badges */}
            <div className="flex flex-wrap justify-center gap-2">
              {job.analysis?.acquisitionMethod && (
                <Badge variant="secondary">{job.analysis.acquisitionMethod.replace(/_/g, ' ')}</Badge>
              )}
              {job.analysis?.analysisScope && <Badge variant="outline">{job.analysis.analysisScope} scope</Badge>}
              <Badge variant="outline">{job.mode.replace(/_/g, ' ')}</Badge>
            </div>

            {/* Progress bar */}
            {job.progress != null && !isFailed && job.status !== 'complete' && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{job.progressDetail || 'Progress'}</span>
                  <span>{Math.round(job.progress)}%</span>
                </div>
                <Progress value={job.progress} />
              </div>
            )}

            {/* Status timeline */}
            <div className="space-y-0">
              {STATUS_STAGES.map((stage, idx) => {
                let state: 'done' | 'active' | 'pending' = 'pending';
                if (isFailed) {
                  if (idx < currentStageIdx) state = 'done';
                  else if (idx === currentStageIdx) state = 'active';
                } else if (job.status === 'complete') {
                  state = 'done';
                } else {
                  if (idx < currentStageIdx) state = 'done';
                  else if (idx === currentStageIdx) state = 'active';
                }

                return (
                  <div key={stage.key} className="flex items-start gap-3 relative">
                    {/* Connector line */}
                    {idx < STATUS_STAGES.length - 1 && (
                      <div
                        className={cn(
                          'absolute left-[9px] top-[24px] w-0.5 h-[calc(100%-4px)]',
                          state === 'done' || (state === 'active' && !isFailed) ? 'bg-primary/30' : 'bg-muted',
                        )}
                      />
                    )}
                    <div className="py-2">
                      <StageIcon state={state} />
                    </div>
                    <div className="py-2 flex-1 min-w-0">
                      <p className={cn('text-sm font-medium', state === 'pending' && 'text-muted-foreground')}>{stage.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Warnings */}
            {job.warnings && job.warnings.length > 0 && (
              <div className="rounded-lg border border-yellow-300/50 bg-yellow-50 dark:bg-yellow-900/10 p-3 space-y-1">
                {job.warnings.map((w, i) => (
                  <p key={i} className="text-sm text-yellow-800 dark:text-yellow-200 flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    {w}
                  </p>
                ))}
              </div>
            )}

            {/* Error state */}
            {isFailed && job.error && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
                <p className="text-sm text-destructive">{job.error}</p>
                <Button asChild variant="outline" size="sm" className="mt-3">
                  <Link to="/">Try Again</Link>
                </Button>
              </div>
            )}

            {/* Complete redirect notice */}
            {job.status === 'complete' && (
              <p className="text-center text-sm text-muted-foreground animate-pulse">Redirecting to results...</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
